import type { Rng } from './rng';
import type { Tunables } from './tunables';
import type { Player } from './types';

export type RunMove = 'jukeLeft' | 'jukeRight' | 'spin' | 'truck';

function clamp(v: number, lo: number, hi: number): number {
  return Math.min(hi, Math.max(lo, v));
}

function avg(players: Player[], key: 'BLK' | 'PRS'): number {
  if (players.length === 0) return 0;
  return players.reduce((n, p) => n + p.ratings[key], 0) / players.length;
}

/** pocketTime = 2.6 + (OL.BLK avg - DL.PRS avg) / 40, clamp 1.6 to 4.0 (GDD section 7). */
export function pocketTime(ol: Player[], dl: Player[], t: Tunables): number {
  const p = t.pocket;
  return clamp(p.base + (avg(ol, 'BLK') - avg(dl, 'PRS')) * p.perPoint, p.min, p.max);
}

/** Pressure penalty for a throw at this time: 0 clean, 0.15 after the pocket breaks, 0.30 in the last moment before the sack. */
export function pressurePenalty(throwTime: number, pocket: number, t: Tunables): number {
  const p = t.pocket;
  const sack = pocket + p.sackAfterSeconds;
  if (throwTime >= sack - p.lateWindowSeconds) return p.latePenalty;
  if (throwTime >= pocket) return p.pressurePenalty;
  return 0;
}

/** Carrier top speed in yards per second (GDD section 7), sprint adds 12 percent while stamina lasts. */
export function carrierSpeed(p: Player, sprinting: boolean, t: Tunables): number {
  const base = t.movement.baseSpeedYps + t.movement.perSpdYps * p.ratings.SPD;
  return sprinting ? base * (1 + t.run.sprintBonus) : base;
}

export interface BreakInputs {
  carrier: Player;
  tackler: Player;
  /** The carrier's last move, or null. */
  move: RunMove | null;
  /** Seconds since that move started. */
  moveAge: number;
  stamina: number;
  /** Other defenders close enough to join the tackle. */
  extraDefenders: number;
}

export interface BreakDetail {
  chance: number;
  /** 'timed', 'late' (move too early to count), or 'none'. */
  timing: 'timed' | 'early' | 'none';
}

/**
 * breakChance (GDD section 7). With a move: 20 + 0.5 x (stat - TAK) + timing + fatigue, clamp 5 to 85.
 * Timing: +25 inside 0.4 s before contact, -10 when the move is older than 1.0 s. No move: 10 + 0.3 x (STR - TAK), clamp 3 to 40.
 * Each extra defender in the tackle: -15.
 */
export function breakChance(b: BreakInputs, t: Tunables): BreakDetail {
  const r = t.run;
  const gang = b.extraDefenders * r.gangPenalty;
  const tak = b.tackler.ratings.TAK;
  if (!b.move || b.moveAge > r.staleAfterSeconds * 2) {
    const c = r.noMoveBase + r.noMovePerPoint * (b.carrier.ratings.STR - tak) - gang;
    return { chance: clamp(c, r.noMoveMin, r.noMoveMax), timing: 'none' };
  }
  const stat = b.move === 'truck' ? b.carrier.ratings.STR : b.carrier.ratings.AGI;
  let timing: BreakDetail['timing'] = 'early';
  let bonus = 0;
  if (b.moveAge <= r.timedWindowSeconds) {
    timing = 'timed';
    bonus = r.timedBonus;
  } else if (b.moveAge > r.staleAfterSeconds) {
    bonus = -10;
  }
  const fatigue = b.stamina < r.fatigueBelow ? r.fatiguePenalty : 0;
  const c = r.breakBase + r.breakPerPoint * (stat - tak) + bonus + fatigue - gang;
  return { chance: clamp(c, r.breakMin, r.breakMax), timing };
}

export interface TackleRoll extends BreakDetail {
  roll: number;
  broke: boolean;
}

export function rollTackle(b: BreakInputs, rng: Rng, t: Tunables): TackleRoll {
  const d = breakChance(b, t);
  const { roll, hit } = rng.rollDetailed(d.chance);
  return { ...d, roll, broke: hit };
}

/** How long a block holds: base + (BLK - PRS or TAK) x perPoint, clamped. */
export function blockSeconds(blocker: Player, defender: Player, t: Tunables): number {
  const r = t.run;
  const def = defender.pos === 'DL' ? defender.ratings.PRS : defender.ratings.TAK;
  return clamp(r.blockBaseSeconds + (blocker.ratings.BLK - def) * r.blockPerPoint, r.blockMinSeconds, r.blockMaxSeconds);
}
